import { ReactNode } from 'react'
import { Stack, Typography } from '@mui/material'

type SettingsHeadingProps = {
  title: ReactNode
  /** Kept to a readable line length. */
  description?: ReactNode
  /** Buttons shown on the right. Put the one primary action here. */
  actions?: ReactNode
  /** `page` is the title at the top of a settings page; `section` heads a group of settings on it. */
  level?: 'page' | 'section'
  /**
   * The heading element, when it shouldn't follow the level: `page` is an
   * `<h1>` and `section` an `<h2>`. For a page-sized title that sits under
   * another page's `<h1>`, so the document keeps a single `<h1>`.
   */
  component?: 'h1' | 'h2' | 'h3'
  /** Put on the heading element, so a section can point `aria-labelledby` at it. */
  id?: string
}

// Smaller, heavier and tighter than MUI's h4/h6, which read as thin and
// oversized next to the rest of the app.
const levelStyles = {
  page: {
    component: 'h1',
    gap: 0.75,
    title: { fontSize: '1.75rem', lineHeight: 1.2, letterSpacing: '-0.02em' },
    description: { fontSize: '0.9375rem' },
  },
  section: {
    component: 'h2',
    gap: 0.25,
    title: { fontSize: '1rem', lineHeight: 1.4, letterSpacing: '-0.005em' },
    description: { fontSize: '0.875rem' },
  },
} as const

/**
 * The title, description and actions row at the top of a settings page and
 * at the top of each section. Both levels share one shape, so every page
 * reads the same way; only the size and heading level change.
 *
 * You rarely use this directly. `SettingsPage` and `SettingsSection` render
 * it for you.
 */
export default function SettingsHeading({
  title,
  description,
  actions,
  level = 'page',
  component,
  id,
}: SettingsHeadingProps) {
  const styles = levelStyles[level]

  return (
    <Stack
      direction="row"
      alignItems="flex-end"
      justifyContent="space-between"
      columnGap={3}
      rowGap={1.5}
      flexWrap="wrap"
      useFlexGap
      data-settings-heading={level}
    >
      <Stack spacing={styles.gap} sx={{ flex: '1 1 320px', minWidth: 0 }}>
        <Typography
          component={component ?? styles.component}
          id={id}
          sx={{ ...styles.title, fontWeight: 600, m: 0 }}
        >
          {title}
        </Typography>
        {description && (
          <Typography
            color="text.secondary"
            sx={{ ...styles.description, lineHeight: 1.5, maxWidth: '65ch' }}
          >
            {description}
          </Typography>
        )}
      </Stack>

      {actions && (
        <Stack direction="row" spacing={1} alignItems="center">
          {actions}
        </Stack>
      )}
    </Stack>
  )
}
